import type * as React from "react";
import { Navigate, useNavigate } from "react-router";
import { ButtonLink, Card } from "@mandrac/ui";
import { useI18n } from "../../lib/i18n";
import type { Me } from "../../lib/session";
import { adminPage, guardPage, type AdminPage, type AdminPageId } from "./access";
import { useAdmin } from "./AdminLayout";

/** "Nemate pristup ovoj stranici", with a link to the first page the user may open. */
export function NoAccess({ me, page, fallback }: { me: Me; page: AdminPage; fallback: AdminPage | undefined }) {
  const { t, tx } = useI18n();
  const navigate = useNavigate();
  const roles = [...me.roles.map((r) => tx(`role.${r}`)), ...(me.isPlatformAdmin && !me.roles.includes("platform_admin") ? [t("role.platform_admin")] : [])];
  return (
    <Card className="flex max-w-2xl flex-col items-start gap-3" data-testid="no-access">
      <h1 className="m-0 text-28 font-extrabold">{t("nav.noAccess.title")}</h1>
      <p className="m-0 text-15 text-muted">
        {roles.length && fallback ? t("nav.noAccess.body", { page: t(page.navKey), roles: roles.join(", ") }) : t("nav.noAccess.noPages")}
      </p>
      {fallback ? (
        <ButtonLink
          variant="primary"
          href={fallback.path}
          onClick={(e) => {
            e.preventDefault();
            navigate(fallback.path);
          }}
        >
          {t("nav.noAccess.back", { page: t(fallback.navKey) })}
        </ButtonLink>
      ) : null}
    </Card>
  );
}

/** Route guard for office pages; roles come from `access.ts`, like the nav. */
export function RequirePage({ page, children }: { page: AdminPageId; children: React.ReactNode }) {
  const { me } = useAdmin();
  const g = guardPage(me, page);
  if (g.kind === "allow") return children;
  if (g.kind === "redirect") return <Navigate to={g.to} replace />;
  return <NoAccess me={me} page={adminPage(page)} fallback={g.fallback} />;
}
